import type { EditorClip } from '../models/editor-clip.model';
import type {
  EditorTextDesignFrame,
  EditorTextDesignSampleText,
  EditorTextDesignTextOverrides,
  EditorTextDesignTextSlot,
} from '../models/editor-text-design.model';

/** Resolve the exact copy rendered by every text layer, including intentional blanks. */
export function resolveEditorTextDesignText(clip: EditorClip): EditorTextDesignSampleText {
  const sample = clip.textDesign?.sampleText;
  const overrides = clip.textDesignText ?? {};
  return {
    eyebrow: overrides.eyebrow ?? sample?.eyebrow,
    primary: String(clip.text ?? sample?.primary ?? clip.title ?? ''),
    secondary: overrides.secondary ?? sample?.secondary,
    badge: overrides.badge ?? sample?.badge,
  };
}

export function resolveEditorTextDesignSlot(
  clip: EditorClip,
  slot: EditorTextDesignTextSlot,
): string {
  return String(resolveEditorTextDesignText(clip)[slot] ?? '');
}

/** Build one shallow clip.update patch without mutating the embedded catalog preset. */
export function buildEditorTextDesignSlotPatch(
  clip: EditorClip,
  slot: EditorTextDesignTextSlot,
  value: string,
): Pick<EditorClip, 'text'> | { textDesignText: EditorTextDesignTextOverrides } {
  if (slot === 'primary') return { text: value };
  return {
    textDesignText: {
      ...(clip.textDesignText ?? {}),
      [slot]: value,
    },
  };
}

/**
 * Remove one editable text box from a clip-local Text Design without mutating the
 * bundled catalog preset. Shape and decoration layers remain grouped with the clip,
 * and the returned patch travels through the normal undoable `clip.update` path.
 */
export function buildEditorTextDesignLayerRemovalPatch(
  clip: EditorClip,
  layerId: string,
): Pick<EditorClip, 'textDesign'> | null {
  const preset = clip.textDesign;
  if (!preset) return null;
  const target = preset.layers.find(layer => layer.id === layerId);
  if (!target || target.kind !== 'text') return null;
  return {
    textDesign: {
      ...preset,
      layers: preset.layers.filter(layer => layer.id !== layerId),
    },
  };
}

/**
 * Update one clip-local Text Design text box without mutating the bundled catalog
 * preset. A corner resize may also supply a new font size so the glyphs grow/shrink
 * with the box; edge resize deliberately changes only wrapping space.
 */
export function buildEditorTextDesignLayerFramePatch(
  clip: EditorClip,
  layerId: string,
  frame: EditorTextDesignFrame,
  fontSize?: number,
): Pick<EditorClip, 'textDesign' | 'textStyle'> | Pick<EditorClip, 'textDesign'> | null {
  const preset = clip.textDesign;
  if (!preset) return null;
  const target = preset.layers.find(layer => layer.id === layerId);
  if (!target || target.kind !== 'text') return null;
  const nextFontSize = typeof fontSize === 'number' && Number.isFinite(fontSize)
    ? Math.max(4, fontSize)
    : null;
  const layers = preset.layers.map(layer => {
    if (layer.id !== layerId || layer.kind !== 'text') return layer;
    return {
      ...layer,
      frame: { ...frame },
      style: nextFontSize === null
        ? layer.style
        : { ...layer.style, fontSize: nextFontSize },
    };
  });
  const textDesign = { ...preset, layers };
  if (target.slot !== 'primary' || nextFontSize === null) return { textDesign };
  return {
    textDesign,
    // The primary inspector override wins over the preset in the renderer. Keep its
    // fontSize synchronized so a corner drag looks identical in preview and export.
    textStyle: { ...(clip.textStyle ?? {}), fontSize: nextFontSize },
  };
}
